import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import DocumentStatusBadge from '../../document-management/components/DocumentStatusBadge';
import { useAuth } from '../../../contexts/AuthContext';
import { documentService } from '../../../services/documentService';
import { siteService } from '../../../services/siteService';
import UploadDocumentModal from './UploadDocumentModal';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentsTab = ({ siteId, documents, onRefresh, isReadOnly = false }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [site, setSite] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  React.useEffect(() => {
    if (siteId) {
      loadSite();
    }
  }, [siteId]);

  const loadSite = async () => {
    let result = await siteService?.getSiteById(siteId);
    if (result?.data) {
      setSite(result?.data);
    }
  };

  const handleFileSelect = async (e, documentId, hasFile) => {
    const file = e?.target?.files?.[0];
    if (!file || !site) return;

    setUploadError(null);
    setUploadingDocId(documentId);

    try {
      // Get document to get file_path
      const docResult = await documentService?.getDocumentById(documentId);
      const document = docResult?.data;

      let result;
      if (hasFile && document?.file_path) {
        // Replace existing file
        result = await documentService?.replaceDocumentFile(
          file,
          documentId,
          site?.company_id,
          siteId,
          document?.file_path
        );
      } else {
        // Upload new file
        result = await documentService?.uploadDocumentFile(
          file,
          documentId,
          site?.company_id,
          siteId
        );
      }

      if (result?.error) {
        setUploadError(result?.error?.message);
      } else {
        // Refresh documents list
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (err) {
      console.error('[DocumentsTab] upload file failed:', err);
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setUploadingDocId(null);
      // Reset file input
      e.target.value = '';
    }
  };

  const triggerFileInput = (documentId) => {
    document?.getElementById(`doc-file-input-${documentId}`)?.click();
  };

  const handleUploadDocument = async (uploadData) => {
    try {
      // Create document record first
      const documentData = {
        site_id: siteId,
        company_id: site?.company_id,
        title: uploadData?.title,
        category: uploadData?.category,
        owner: uploadData?.owner,
        issue_date: uploadData?.issueDate,
        expiry_date: uploadData?.expiryDate,
        notes: uploadData?.notes || '',
        file_path: 'temp', // Temporary placeholder
        status: 'valid'
      };

      const createResult = await documentService?.createDocument(documentData);
      if (createResult?.error) {
        throw createResult.error;
      }

      const newDocument = createResult?.data;

      // Upload file and update document record
      const uploadResult = await documentService?.uploadDocumentFile(
        uploadData?.file,
        newDocument?.id,
        site?.company_id,
        siteId
      );

      if (uploadResult?.error) {
        // If file upload fails, delete the document record
        await documentService?.deleteDocument(newDocument?.id);
        throw uploadResult.error;
      }

      // Refresh documents list
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">{t('siteDocuments')}</h3>
        {userProfile?.role === 'admin' && !isReadOnly && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              iconName="Upload"
              onClick={() => setIsUploadModalOpen(true)}
            >
              {t('uploadDocument')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              iconName="Plus"
              onClick={() => navigate('/document-management')}
            >
              {t('addDocument')}
            </Button>
          </div>
        )}
      </div>
      {uploadError && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" />
          <div className="flex-1">
            <p className="text-sm text-error font-medium">{t('uploadError')}</p>
            <p className="text-sm text-error/80">{uploadError}</p>
          </div>
          <button
            onClick={() => setUploadError(null)}
            className="text-error hover:text-error/80"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      )}
      {documents?.length > 0 ? (
        <div className="bg-muted/50 rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('titleHeader')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('category')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('status')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('expiryDate')}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('owner')}</th>
                  {userProfile?.role === 'admin' && (
                    <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">{t('actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {documents?.map((doc) => {
                  // H&S Docs from rams_documents are merged in for visibility
                  // (GIVolooT hotfix), but the per-document detail route only
                  // knows how to look up by documents.id today. Skip the
                  // click-through navigation for rams rows until the storage
                  // consolidation under 5m1glArl ships.
                  const isRamsRow = typeof doc?.id === 'string' && doc?.id?.startsWith('rams:');
                  const handleRowClick = isRamsRow
                    ? undefined
                    : () => navigate(`/sites/${siteId}/documents/${doc?.id}`);
                  const cellClass = `px-4 py-3 ${isRamsRow ? '' : 'cursor-pointer'}`;
                  return (
                  <tr
                    key={doc?.id}
                    className="hover:bg-background transition-smooth"
                  >
                    <td
                      className={cellClass}
                      onClick={handleRowClick}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="FileText" size={16} color="var(--color-accent)" />
                        <span className="text-sm text-foreground">{doc?.title}</span>
                      </div>
                    </td>
                    <td
                      className={cellClass}
                      onClick={handleRowClick}
                    >
                      <span className="text-sm text-foreground capitalize">{doc?.category?.replace('_', ' ')}</span>
                    </td>
                    <td
                      className={cellClass}
                      onClick={handleRowClick}
                    >
                      <DocumentStatusBadge
                        status={doc?.status}
                        daysUntilExpiry={doc?.expiry_date ? Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)) : null}
                      />
                    </td>
                    <td
                      className={cellClass}
                      onClick={handleRowClick}
                    >
                      <span className="text-sm text-foreground">{doc?.expiry_date ? new Date(doc?.expiry_date)?.toLocaleDateString() : '—'}</span>
                    </td>
                    <td
                      className={cellClass}
                      onClick={handleRowClick}
                    >
                      <span className="text-sm text-foreground">{doc?.owner || '—'}</span>
                    </td>
                    {userProfile?.role === 'admin' && !isReadOnly && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <input
                            id={`doc-file-input-${doc?.id}`}
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileSelect(e, doc?.id, !!doc?.file_path)}
                            className="hidden"
                          />
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              triggerFileInput(doc?.id);
                            }}
                            disabled={uploadingDocId === doc?.id}
                            className="p-2 hover:bg-accent/10 rounded-md transition-smooth disabled:opacity-50"
                            title={doc?.file_path ? 'Replace file' : 'Upload file'}
                            aria-label={doc?.file_path ? 'Replace file' : 'Upload file'}
                          >
                            {uploadingDocId === doc?.id ? (
                              <Icon name="Loader2" size={16} className="text-accent animate-spin" />
                            ) : (
                              <Icon name="Upload" size={16} color="var(--color-accent)" />
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-muted/50 rounded-lg p-12 border border-border text-center">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-foreground mb-2">{t('noDocumentsYet')}</h4>
          <p className="text-sm text-muted-foreground mb-4">{t('noDocumentsDesc')}</p>
          {userProfile?.role === 'admin' && !isReadOnly && (
            <Button
              size="sm"
              iconName="Upload"
              onClick={() => setIsUploadModalOpen(true)}
            >
              {t('uploadDocument')}
            </Button>
          )}
        </div>
      )}

      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadDocument}
        siteId={siteId}
        companyId={site?.company_id}
      />
    </div>
  );
};

export default DocumentsTab;